<script lang="ts">
	import PageGrid16 from '$lib/storyblok/components/pageGrid16.svelte';
	import type { SbImage } from '$lib/storyblok/types';
	import { getContext, onMount } from 'svelte';
	import Image from '$lib/storyblok/components/image.svelte';
	import { formatHeadingDate } from '$lib/storyblok/blogHelpers';
	import { fade } from 'svelte/transition';
	import Button from '$lib/buttons/v3/button.svelte';
	import { slideIn } from '$lib/actions/slideIn';
	import type { ISbStoryData } from '@storyblok/js';
	import { editable } from '$lib/storyblok/editable';
	import { storyblok } from '$lib/state/storyblok.svelte';

	interface Props {
		content: ISbStoryData<{
			title: string;
			default_image: SbImage;
			stories: Array<ISbStoryData>;
		}>['content'];
	}

	let { content }: Props = $props();

	let latestStories = $state<Array<ISbStoryData>>();

	onMount(async () => {
		if (content.stories.length === 0) {
			const { data } = await storyblok.client.getStories({
				version: 'published',
				by_slugs: `blog/*`,
				filter_query: {
					component: {
						in: 'blog_page'
					}
				},
				sort_by: 'content.custom_publish_date:desc',
				per_page: 3
			});

			latestStories = data.stories;
		} else {
			const { data } = await storyblok.client.getStories({
				version: 'published',
				by_uuids: content.stories.join(',')
			});

			latestStories = data.stories;
		}
	});

	let imageSwitchTimeout: NodeJS.Timeout;

	let previewImage = $state(content.default_image);
</script>

<section use:editable={content} class="relative bg-dark-grey px-5 py-16 text-white lg:py-32">
	<PageGrid16>
		<div class="col-span-full flex flex-col justify-between gap-y-8 lg:col-start-2 lg:col-end-8">
			<div>
				<h1
					use:slideIn={{ threshold: 0.5, duration: 600 }}
					class="text-[36px]/[112%] font-semibold tracking-[-.36px] opacity-[0.01] lg:text-[48px]/[110%] lg:tracking-[-.48px]"
				>
					{content.title}
				</h1>
				<div class="mt-6 flex flex-col gap-y-8 lg:mt-12">
					{#if latestStories}
						{#each latestStories as story, i}
							<a
								use:slideIn={{ threshold: 0.5, duration: 600, delay: 80 * (i + 1) }}
								target="_blank"
								href="/{story.full_slug}"
								onmouseenter={() => {
									clearTimeout(imageSwitchTimeout);
									previewImage = story.content.hero_image;
								}}
								onmouseleave={() => {
									imageSwitchTimeout = setTimeout(
										() => (previewImage = content.default_image),
										250
									);
								}}
								class="group opacity-[0.01]"
							>
								{#if story.content.hero_image?.filename}
									<Image
										class="mb-3 aspect-4/3 h-full w-full rounded-2xl object-cover lg:hidden"
										src={story.content.hero_image.filename}
										alt={story.content.hero_image.alt}
										title={story.content.hero_image.title}
										width={800}
									/>
								{/if}
								<p
									class="text-[14px]/[160%] opacity-[.68] transition-opacity group-hover:opacity-100 lg:text-[16px]/[150%]"
								>
									{formatHeadingDate(story.content.custom_publish_date || story.first_published_at)}
								</p>
								<p
									class="mt-1 text-[20px]/[140%] tracking-[-.2px] lg:text-[24px] lg:tracking-[-.24px]"
								>
									{story.content.title}
								</p>
							</a>
						{/each}
					{/if}
				</div>
			</div>
			<div
				use:slideIn={{ threshold: 0.5, delay: 320, duration: 600 }}
				class="opacity-[0.01] lg:flex"
			>
				<Button href="/blog" target="_blank" class="btn-white">View all</Button>
			</div>
		</div>

		<div
			use:slideIn={{ threshold: 0.5, delay: 400, duration: 600 }}
			class="opacity-[0.01] max-lg:hidden lg:col-start-9 lg:col-end-[15]"
		>
			{#key previewImage}
				<div in:fade={{ duration: 750 }}>
					<Image
						class="aspect-square h-full w-full rounded-2xl object-cover"
						src={previewImage.filename}
						alt={previewImage.alt}
						title={previewImage.title}
						width={1200}
					/>
				</div>
			{/key}
		</div>
	</PageGrid16>
</section>
